import { AI_FORMULA } from "./aiFormula";
export interface LonghandGuideScreenshot {
  id: string;
  src: string;
  width: number;
  height: number;
  alt: string;
  caption: string;
  provenance: string;
}

export interface LonghandGuideStep {
  id: string;
  aliases?: string[];
  kicker: string;
  instruction: string;
  actions: string[];
  expected: string;
  verify: string[];
  recover: string[];
  promptSlideIds?: string[];
  relatedSlideIds?: string[];
  screenshotIds?: string[];
  stop?: string;
}

export interface LonghandGuideSession {
  id: 1 | 2 | 3 | 4 | 5;
  why: string;
  startWith: string;
  stopRule: string;
  decisionQuestion: string;
  steps: LonghandGuideStep[];
}

export const LONGHAND_GUIDE_SCREENSHOTS: Record<string, LonghandGuideScreenshot> = {
  "chat-home": {
    id: "chat-home",
    src: "/screenshots/ai-agent-starter/chatgpt-work-home.jpg",
    width: 1036,
    height: 560,
    alt: "ChatGPT Work home screen with the message box, tools, recent work, and navigation visible.",
    caption: "Start in your organization's approved AI workspace and confirm you are signed into the intended account.",
    provenance: "Existing authenticated ChatGPT Work product capture from the repository's AI Agent Starter guide; inspected for personal data on 2026-09-04.",
  },
  "model-picker": {
    id: "model-picker",
    src: "/screenshots/masterclass/02-chat-tour.png",
    width: 1337,
    height: 852,
    alt: "Claude chat home screen with the conversation area, new chat control, and model picker visible.",
    caption: "Open the model picker in the approved workspace and choose the lightest model that can pass the Job's check.",
    provenance: "Existing authenticated Claude product capture from the repository's Claude Masterclass; inspected for personal data on 2026-09-04.",
  },
  "data-controls": {
    id: "data-controls",
    src: "/screenshots/ai-agent-starter/chatgpt-data-controls.jpg",
    width: 1550,
    height: 964,
    alt: "ChatGPT settings open to Data controls, including model improvement, shared links, and data export controls.",
    caption: "Find the data controls in your approved AI workspace before adding work information. Your organization's policy, not this example screen, determines the right settings.",
    provenance: "Existing authenticated ChatGPT Work product capture from the repository's AI Agent Starter guide; inspected for personal data on 2026-09-04.",
  },
  "create-project": {
    id: "create-project",
    src: "/screenshots/ai-agent-starter/chatgpt-create-project.jpg",
    width: 760,
    height: 450,
    alt: "ChatGPT Create project dialog with fields for a project name and default memory.",
    caption: "A dedicated Project can keep one workflow's instructions, approved files, and reviewed examples together.",
    provenance: "Existing authenticated ChatGPT Work product capture from the repository's AI Agent Starter guide; inspected for personal data on 2026-09-04.",
  },
  personalization: {
    id: "personalization",
    src: "/screenshots/ai-agent-starter/chatgpt-personalization.jpg",
    width: 710,
    height: 635,
    alt: "ChatGPT Personalization settings with style controls and a Custom instructions field.",
    caption: "Standing instructions are one possible home for reusable context. A project or Skill is usually easier to inspect when the workflow grows.",
    provenance: "Existing authenticated ChatGPT Work product capture from the repository's AI Agent Starter guide; inspected for personal data on 2026-09-04.",
  },
  "prompt-result": {
    id: "prompt-result",
    src: "/screenshots/masterclass/03-good-prompt.png",
    width: 1337,
    height: 852,
    alt: "Claude conversation showing a prompt with a requested outcome, context, and example tone, followed by a three-sentence draft.",
    caption: "A concrete outcome, context, and example give the reviewer something specific to accept or correct.",
    provenance: "Existing authenticated Claude product capture from the repository's Claude Masterclass; uses synthetic work content and was inspected for personal data on 2026-09-04.",
  },
  "add-sources": {
    id: "add-sources",
    src: "/screenshots/masterclass/04-share-files.png",
    width: 1337,
    height: 852,
    alt: "Claude attachment menu showing options for files, screenshots, projects, GitHub, Skills, Connectors, research, and web search.",
    caption: "Use an approved export, file, screenshot, or connection. Choose the narrowest source path that can support the Job.",
    provenance: "Existing authenticated Claude product capture from the repository's Claude Masterclass; inspected for personal data on 2026-09-04.",
  },
  "artifact-library": {
    id: "artifact-library",
    src: "/screenshots/masterclass/05-artifacts.png",
    width: 1337,
    height: 852,
    alt: "Claude Artifacts library with a New artifact button and saved creations.",
    caption: "The Artifact keeps the deliverable separate from the conversation used to make it.",
    provenance: "Existing authenticated Claude product capture from the repository's Claude Masterclass; inspected for personal data on 2026-09-04.",
  },
  "project-library": {
    id: "project-library",
    src: "/screenshots/masterclass/06-project-setup.png",
    width: 1337,
    height: 852,
    alt: "Claude Projects gallery with a New project button and existing project cards.",
    caption: "Keep each Project focused on one kind of work so its sources, instructions, and reviewed examples stay inspectable.",
    provenance: "Existing authenticated Claude product capture from the repository's Claude Masterclass; inspected for personal data on 2026-09-04.",
  },
  "agent-workspace": {
    id: "agent-workspace",
    src: "/screenshots/masterclass/27-cowork-intro.png",
    width: 1337,
    height: 852,
    alt: "Claude Cowork introduction showing a folder picker for handing off a complex task.",
    caption: "An agent workspace can coordinate several steps across approved files and tools. Access to a folder does not grant authority to act outside the Job.",
    provenance: "Existing authenticated Claude Cowork product capture from the repository's Claude Masterclass; inspected for personal data on 2026-09-04.",
  },
  "connector-permissions": {
    id: "connector-permissions",
    src: "/screenshots/ai-agent-starter/chatgpt-plugins.jpg",
    width: 710,
    height: 635,
    alt: "ChatGPT Plugins settings showing installed tools and a permissions control.",
    caption: "Inspect the permission policy for every connected tool. Start with asking or read-only behavior when the product supports it.",
    provenance: "Existing authenticated ChatGPT Work product capture from the repository's AI Agent Starter guide; inspected for personal data on 2026-09-04.",
  },
  "cloud-browser": {
    id: "cloud-browser",
    src: "/screenshots/ai-agent-starter/chatgpt-cloud-browser.jpg",
    width: 710,
    height: 635,
    alt: "ChatGPT Cloud browser settings showing default permissions set to Always ask and an Add site control.",
    caption: "Browser access should be explicit and site-scoped. Product labels change, so verify the live permission before every first run.",
    provenance: "Existing authenticated ChatGPT Work product capture from the repository's AI Agent Starter guide; inspected for personal data on 2026-09-04.",
  },
  "install-extension": {
    id: "install-extension",
    src: "/screenshots/masterclass/11-install-extension.png",
    width: 1337,
    height: 852,
    alt: "Chrome Web Store page for the verified Claude extension published by Anthropic.",
    caption: "Install only the extension approved by your organization and verify the publisher before continuing.",
    provenance: "Existing Chrome Web Store capture from the repository's Claude Masterclass; inspected for personal data on 2026-09-04.",
  },
  "site-access": {
    id: "site-access",
    src: "/screenshots/masterclass/13-grant-site-access.png",
    width: 1337,
    height: 852,
    alt: "Claude in Chrome settings open to Site permissions with a default policy selector.",
    caption: "Grant one-site or one-time access for the test. Remove it when the Job no longer needs it.",
    provenance: "Existing authenticated Claude in Chrome product capture from the repository's Claude Masterclass; inspected for personal data on 2026-09-04.",
  },
  "adaptive-reasoning": {
    id: "adaptive-reasoning",
    src: "/screenshots/masterclass/07-adaptive-reasoning.png",
    width: 1337,
    height: 852,
    alt: "Claude settings with the Instructions for Claude field containing the Adaptive Reasoning Protocol.",
    caption: "Paste the protocol into the reusable instructions field, then save the change before testing it.",
    provenance: "Existing authenticated Claude product capture from the repository's Claude Masterclass; inspected for personal data on 2026-09-04.",
  },
  "claude-privacy": {
    id: "claude-privacy",
    src: "/screenshots/masterclass/09-privacy.png",
    width: 1337,
    height: 852,
    alt: "Claude settings open to privacy controls.",
    caption: "Review the live privacy controls in the approved account before entering work information.",
    provenance: "Existing authenticated Claude product capture from the repository's Claude Masterclass; inspected for personal data on 2026-09-04.",
  },
  connections: {
    id: "connections",
    src: "/screenshots/masterclass/21-connections-mcp.png",
    width: 1337,
    height: 852,
    alt: "Claude Connectors panel showing available tools and per-tool permission controls.",
    caption: "Open the connector settings, select one approved source, and inspect its permission controls before connecting it.",
    provenance: "Existing authenticated Claude product capture from the repository's Claude Masterclass; inspected for personal data on 2026-09-04.",
  },
  "skills-features": {
    id: "skills-features",
    src: "/screenshots/masterclass/22-skills-features.png",
    width: 1337,
    height: 852,
    alt: "Claude capabilities settings with file creation and code execution controls.",
    caption: "Enable only the capabilities the selected Job needs, then confirm each permission during the first run.",
    provenance: "Existing authenticated Claude product capture from the repository's Claude Masterclass; inspected for personal data on 2026-09-04.",
  },
  "skills-library": {
    id: "skills-library",
    src: "/screenshots/masterclass/23-install-skills.png",
    width: 1337,
    height: 852,
    alt: "Claude Skills library with an installed Skill open and its trigger visible.",
    caption: "Install the finished Skill in the intended Project, confirm it is enabled, and test its trigger.",
    provenance: "Existing authenticated Claude product capture from the repository's Claude Masterclass; inspected for personal data on 2026-09-04.",
  },
  "code-app": {
    id: "code-app",
    src: "/screenshots/masterclass/31-app-tour.png",
    width: 1337,
    height: 852,
    alt: "Claude Code desktop app showing the new task, routines, recent work, and pull request areas.",
    caption: "Open only the folder or workspace assigned to the Job before starting a Claude Code task.",
    provenance: "Existing authenticated Claude Code product capture from the repository's Claude Masterclass; inspected for personal data on 2026-09-04.",
  },
  "code-download": {
    id: "code-download",
    src: "/screenshots/masterclass/32-install-code.png",
    width: 1337,
    height: 852,
    alt: "Official Claude Code download page with a Download for macOS button.",
    caption: "Install Claude Code from the approved official source for your operating system.",
    provenance: "Existing public Anthropic product page capture from the repository's Claude Masterclass; inspected for personal data on 2026-09-04.",
  },
  "code-safety": {
    id: "code-safety",
    src: "/screenshots/masterclass/35-code-safety.png",
    width: 1337,
    height: 852,
    alt: "Claude Code settings showing model controls and the Accept edits control.",
    caption: "Keep edit approval visible and inspect each proposed change before accepting it.",
    provenance: "Existing authenticated Claude Code product capture from the repository's Claude Masterclass; inspected for personal data on 2026-09-04.",
  },
  "github-open-with-desktop": {
    id: "github-open-with-desktop",
    src: "/screenshots/longhand-ai-program/github-open-with-desktop.png",
    width: 836,
    height: 766,
    alt: "GitHub repository Code menu with Open with GitHub Desktop highlighted.",
    caption: "From a repository's Code menu, choose Open with GitHub Desktop to bring the approved project onto your computer.",
    provenance: "Official GitHub Docs interface capture downloaded from the repository-cloning guide on 2026-09-04.",
  },
  "github-clone-location": {
    id: "github-clone-location",
    src: "/screenshots/longhand-ai-program/github-clone-location.png",
    width: 1032,
    height: 650,
    alt: "GitHub Desktop Clone a Repository dialog with the Local Path chooser highlighted.",
    caption: "Choose a local folder you can recognize later, then clone the repository into that location.",
    provenance: "Official GitHub Docs interface capture downloaded from the repository-cloning guide on 2026-09-04.",
  },
  "vercel-import-repository": {
    id: "vercel-import-repository",
    src: "/screenshots/longhand-ai-program/vercel-import-repository.png",
    width: 1770,
    height: 1418,
    alt: "Vercel Import Git Repository screen with a connected GitHub account and Import buttons.",
    caption: "In Vercel, select the intended GitHub account and import only the repository you want to preview.",
    provenance: "Official Vercel Docs interface capture downloaded from the Git deployment guide on 2026-09-04.",
  },
};

export const LONGHAND_GUIDE_SESSIONS: LonghandGuideSession[] = [
  {
    id: 1,
    why: "A long prompt is not useful because it is long. It is useful when another person can understand the intended result, the trusted context, the boundary, and how the result will be reviewed.",
    startWith: "An approved AI workspace, a low-risk work example, and a person who can review the result.",
    stopRule: "Use synthetic or redacted information until your organization has approved the tool, account, data, and sharing policy. Never paste credentials or information you are not authorized to share.",
    decisionQuestion: "Can another authorized person reuse the Skill and explain why its result is better than the vague request?",
    steps: [
      {
        id: "privacy-team-rule",
        aliases: ["tool-to-team-thinking", "four-levels", "three-layers", "prompt-multiplier"],
        kicker: "Set the boundary before the context",
        instruction: "Write one rule that covers approved data, allowed actions, and the person who approves consequences.",
        actions: [
          "Confirm that the AI account and workspace are approved for the kind of work you will test.",
          "List the sources the Skill may use and the sensitive information it must never access.",
          "Choose the first-run authority: Read or Draft. Name the person who must approve Change, Send, or Publish.",
          "Paste the registered boundary prompt below into the Skill or project instructions and replace every bracketed field.",
        ],
        expected: "A short boundary that names approved sources, forbidden data, allowed actions, the reviewer, and the evidence retained from a run.",
        verify: [
          "Every source is specific enough to identify.",
          "Consequential actions require a named person.",
          "The first run cannot quietly expand from reading to acting.",
        ],
        recover: [
          "No approved work account: practise with a fictional calendar and synthetic tasks.",
          "No clear policy: stop before adding data or a connection and ask the owner what is permitted.",
        ],
        promptSlideIds: ["privacy-team-rule"],
        screenshotIds: ["data-controls", "claude-privacy"],
        stop: "Stop before entering work data if the account, retention settings, or sharing policy is unclear.",
      },
      {
        id: "who-i-am-prompt",
        kicker: "Give the Skill standing context",
        instruction: "Create one inspectable home for the Chief of Staff and add only the context that changes how it should decide or write.",
        actions: [
          "Create a project, Skill, or equivalent reusable workspace named Chief of Staff.",
          "Add your role, remit, voice, decision filters, current priorities, collaborators, and leadership improvements.",
          "Remove background detail that will not change a decision or the finished brief.",
          "Ask the AI to summarize the standing context and flag anything ambiguous before you use it.",
        ],
        expected: "A reusable context block that is concise enough to inspect and specific enough to change the first draft.",
        verify: [
          "Priorities and decision filters are current.",
          "A teammate could tell which details affect the work.",
          "No confidential detail appears without a clear need and approval.",
        ],
        recover: [
          "Too much context: keep only information that changes priority, tone, boundaries, or approval.",
          "Context will change often: keep volatile priorities in a separate, dated section.",
        ],
        promptSlideIds: ["who-i-am-prompt"],
        screenshotIds: ["create-project", "personalization"],
      },
      {
        id: "atomic-reasoning-protocol",
        kicker: "Add a method, not a demand for hidden reasoning",
        instruction: "Give the Chief of Staff a repeatable way to choose between solving and teaching, then judge the observable result.",
        actions: [
          "Add the registered Adaptive Reasoning Protocol to the reusable instructions.",
          "Give the AI one small analysis task and one small explanation task.",
          "Check that the response structure changes with the request type and that the final answer is supported.",
          "Keep only the protocol language that improves the result; do not treat reported reasoning as proof of correctness.",
        ],
        expected: "A method that produces a decomposed analysis when solving and a plain-language teaching snapshot when explaining.",
        verify: [
          "The response matches the request type.",
          "The conclusion can be checked independently.",
          "The method does not override the source or authority boundary.",
        ],
        recover: [
          "The answer is verbose: ask for the verified conclusion and the evidence that supports it.",
          "The answer is still wrong: return to the source, boundary, or definition of done instead of adding more reasoning language.",
        ],
        promptSlideIds: ["atomic-reasoning-protocol"],
        screenshotIds: ["adaptive-reasoning"],
      },
      {
        id: "outcome-first-prompt-format",
        aliases: ["outcome-first-prompt-example"],
        kicker: "Define the finish line",
        instruction: "Turn the vague request into one outcome-first sentence before choosing any more tools.",
        actions: [
          "Start with “How might we” to keep the problem open long enough to define it.",
          "Choose a precise verb such as compare, prioritize, draft, or design.",
          "Describe the observable outcome and the boundary that must remain true.",
          "Name the approved sources that should ground the work.",
        ],
        expected: `One sentence in the form: ${AI_FORMULA}?`,
        verify: [
          "The verb describes work the AI can actually perform.",
          "The outcome is reviewable rather than aspirational.",
          "The sources and non-negotiable boundary are explicit.",
        ],
        recover: [
          "The outcome sounds like a topic: ask what a reviewer should receive when the work is finished.",
          "The prompt names no evidence: pause and identify a trusted source before running it.",
        ],
        relatedSlideIds: ["outcome-first-prompt-format"],
      },
      {
        id: "outcome-first-prompt-evidence",
        kicker: "Run the before-and-after test",
        instruction: "Compare one vague request with the first version of the reusable Skill using the same low-risk Job.",
        actions: [
          "Run your original vague request and save the result without polishing it.",
          "Run the improved request with standing context, approved sources, boundaries, and a review standard.",
          "Compare the two results for grounding, completeness, boundary compliance, and reviewer effort.",
          "Record what improved and what still requires human judgement.",
        ],
        expected: "Two comparable outputs and a short note explaining which concrete changes made the second one easier to review.",
        verify: [
          "Important claims trace to the supplied sources.",
          "The result respects the stated authority.",
          "A reviewer can see what done means without guessing.",
        ],
        recover: [
          "The outputs look the same: strengthen the outcome, source, or review standard one at a time.",
          "The result invents context: require links or citations and ask it to mark unknowns explicitly.",
        ],
        promptSlideIds: ["outcome-first-prompt-evidence"],
        screenshotIds: ["prompt-result"],
      },
      {
        id: "iterate-with-good-examples",
        aliases: ["get-unstuck", "qa-1"],
        kicker: "Teach the next run",
        instruction: "Turn one accepted correction into a reviewed example instead of restarting from scratch.",
        actions: [
          "Mark the parts of the result that worked and name the specific changes required.",
          "If the response is confusing, use the reset prompt before deciding what to edit.",
          "Save the accepted version as GOOD_EXAMPLES with a note about why it is acceptable.",
          "Update the reusable instructions only when the correction should apply to future runs.",
        ],
        expected: "A first Reusable Skill containing standing context, an outcome, boundaries, approved sources, and one reviewed example.",
        verify: [
          "The example was reviewed by a person who understands the Job.",
          "The correction is specific enough for the next run to imitate.",
          "You can explain why the improved result is better.",
        ],
        recover: [
          "Feedback is vague: point to the exact sentence or omission and state the desired change.",
          "A one-off preference is becoming a permanent rule: keep it in the run instead of the Skill.",
        ],
        promptSlideIds: ["get-unstuck"],
      },
    ],
  },
  {
    id: 2,
    why: "Choosing the tool first creates attractive demos that may not solve meaningful work. Evidence from a real week makes the Job, its frequency, and its review point visible before the tool decision.",
    startWith: "The Reusable Skill from Capability 1 and one approved calendar week, export, or redacted screenshot.",
    stopRule: "Do not invent use cases or expose private attendee details. If calendar access is unavailable or unapproved, work from a redacted export or synthetic week.",
    decisionQuestion: "Can you show evidence that this Job repeats, produces a reviewable result, and deserves the first controlled AI run?",
    steps: [
      {
        id: "session-3-job-first",
        kicker: "Begin with work evidence",
        instruction: "Define the recurring progress before discussing products or features.",
        actions: [
          "Open one real week and mark repeated preparation, coordination, follow-up, or review work.",
          "For each pattern, name the trigger, input, output, and person who reviews it.",
          "Exclude work that is rare, undefined, or too consequential for a first run.",
        ],
        expected: "A short evidence list describing where recurring work appeared and what a useful result would look like.",
        verify: [
          "Each candidate points to a real event or artifact.",
          "The output can be reviewed by a named person.",
          "No candidate depends on a tool choice yet.",
        ],
        recover: [
          "Everything looks unique: group events by the progress they require, not by meeting title.",
          "The week is atypical: inspect a second week before ranking.",
        ],
        relatedSlideIds: ["session-3-job-first"],
      },
      {
        id: "session-4-calendar-audit",
        aliases: ["session-3-mine-repeat"],
        kicker: "Ask for evidence, not ideas",
        instruction: "Use the calendar audit to surface three to five candidate Jobs without allowing the AI to fabricate examples.",
        actions: [
          "Choose an approved calendar connection, export, file, or redacted screenshot.",
          "Paste the registered calendar-audit prompt and specify the exact week.",
          "Inspect every proposed Job against the source and remove anything the calendar does not support.",
          "Add a reviewer and risk note to each surviving candidate.",
        ],
        expected: "Three to five candidate Jobs, each with evidence, trigger, approved input, reviewable output, reviewer, and risk.",
        verify: [
          "Every candidate cites visible calendar evidence.",
          "The proposed output moves a real Job forward.",
          "The source path stayed within the approved boundary.",
        ],
        recover: [
          "The tool cannot read the calendar: use a redacted screenshot or export.",
          "The AI gives generic ideas: instruct it to delete any item without direct evidence.",
        ],
        promptSlideIds: ["session-4-calendar-audit"],
        screenshotIds: ["add-sources"],
      },
      {
        id: "session-3-capture-candidates",
        aliases: ["session-3-jtbd-formula"],
        kicker: "Describe the progress",
        instruction: "Rewrite each candidate as a Job to Be Done and make the current workaround visible.",
        actions: [
          "Write: When [situation], I want to [progress], so I can [result].",
          "Add the frequency, input, output, owner, current workaround, value, and risk.",
          "Remove solution language such as a vendor, model, or automation from the Job statement.",
        ],
        expected: "Three to five comparable Job statements that describe progress and a visible result.",
        verify: [
          "The situation creates a recurring need.",
          "The progress is clear without naming a solution.",
          "The result explains how a person will know the Job moved forward.",
        ],
        recover: [
          "The Job is too broad: narrow it to one trigger and one reviewer-visible output.",
          "The Job is a feature request: ask what progress the person needs even if that feature did not exist.",
        ],
        relatedSlideIds: ["session-3-jtbd-formula", "session-3-capture-candidates"],
      },
      {
        id: "rice-dimensions",
        aliases: ["rice-calibration-huddle"],
        kicker: "Rank, then challenge the weak assumption",
        instruction: "Compare each Job on frequency, value, feasibility, and risk, then ask another person what evidence is weakest.",
        actions: [
          "Give each Job a simple relative rating for frequency, value, feasibility, and risk.",
          "Write one evidence note beside every rating instead of relying on a number alone.",
          "Rank the Jobs and ask a partner which assumption they would lower, test, or reject.",
          "Revise the ranking when the evidence changes.",
        ],
        expected: "A ranked candidate list with evidence notes and one challenged assumption for the leading Job.",
        verify: [
          "The ranking can be explained without the score labels.",
          "Risk includes people, trust, money, operations, and compliance.",
          "The first Job is feasible with today's inputs and review path.",
        ],
        recover: [
          "Everything ranks the same: compare the weakest evidence and highest consequence first.",
          "The top Job is high-risk: choose a narrower read-or-draft slice for the first run.",
        ],
        relatedSlideIds: ["rice-dimensions", "rice-calibration-huddle"],
      },
      {
        id: "session-3-job-brief",
        aliases: ["qa-2"],
        kicker: "Update the evolving artifact",
        instruction: "Add the selected Job to the Chief of Staff Skill as a compact operating brief.",
        actions: [
          "Record the trigger that starts the Job.",
          "List the approved sources and the exact output the reviewer should receive.",
          "Choose Read, Draft, Change, or Send or Publish and name the person who approves consequences.",
          "Add the evidence that made this Job rank first and what would cause you to revisit the choice.",
        ],
        expected: "A Selected Job inside the Chief of Staff Skill with trigger, sources, done, authority, reviewer, and ranking evidence.",
        verify: [
          "The Job came from real evidence.",
          "Done is observable by another person.",
          "The first-run authority is no broader than the Job requires.",
        ],
        recover: [
          "The reviewer cannot tell whether it is done: add an output format and acceptance criteria.",
          "The Job needs unavailable data: select the next ranked Job or create an approved manual input.",
        ],
        relatedSlideIds: ["session-3-job-brief"],
      },
    ],
  },
  {
    id: 3,
    why: "The best tool is not the one with the most features. It is the lightest approved path that can reach the trusted source, complete the required action, and leave the important decision with the right person.",
    startWith: "The Selected Job, its source, required action, definition of done, risk, and reviewer from Capability 2.",
    stopRule: "Do not connect a system or grant a permission just to make a tool choice easier. If the source or action is not approved, choose a supervised file, export, or manual path.",
    decisionQuestion: "Can you explain why the selected tool, model, access, and authority fit this Job, and what evidence would change the decision?",
    steps: [
      {
        id: "integration-options",
        kicker: "Choose where the work should happen",
        instruction: "Place the Job in Chat, inside an existing tool, an agent workspace, or browser action before comparing vendors.",
        actions: [
          "Decide whether the work is one question, one deliverable, a multi-step objective, or a recurring outcome.",
          "Identify where the trusted context already lives.",
          "Choose the simplest work surface that can produce the defined output.",
        ],
        expected: "One starting surface with a short reason tied to the size and location of the work.",
        verify: [
          "The surface matches the unit of work.",
          "The source can be reached through an approved path.",
          "A lighter option was considered before a more agentic one.",
        ],
        recover: [
          "The distinction is unclear: run a manual Chat or file-based test before moving to an agent workspace.",
          "The work spans tools but the outcome is still vague: return to the Job Brief before adding integrations.",
        ],
        relatedSlideIds: ["integration-options"],
        screenshotIds: ["agent-workspace"],
      },
      {
        id: "tool-selection-rubric",
        kicker: "Use requirements that survive vendor changes",
        instruction: "Compare options using the size of the work, data location, required action, and risk ownership.",
        actions: [
          "Write one requirement for each of the four rubric questions.",
          "Eliminate tools that cannot reach the approved source or complete the required action.",
          "Eliminate paths that require more authority than the first run allows.",
          "Compare the remaining choices on quality, review effort, and recoverability.",
        ],
        expected: "A short comparison based on Job requirements rather than a feature inventory.",
        verify: [
          "Every criterion traces to the Job Brief.",
          "The comparison includes a viable fallback.",
          "The chosen path can be tested without a consequential action.",
        ],
        recover: [
          "Feature lists are driving the choice: restate the trigger, source, output, and authority first.",
          "No option satisfies the Job: narrow the first-run slice or keep the work manual.",
        ],
        relatedSlideIds: ["tool-selection-rubric"],
      },
      {
        id: "connections-and-tools",
        kicker: "Separate reach from authority",
        instruction: "Inspect the exact connection and permission policy before treating a tool as viable.",
        actions: [
          "Name the exact account, calendar, file, report, inbox, or system the Job needs.",
          "List what the tool must read, draft, search, click, update, or coordinate.",
          "Open the live permission settings and confirm what the tool can actually do.",
          "Choose the narrowest connection and authority that supports a useful first run.",
        ],
        expected: "A source-action-authority map for each viable tool path.",
        verify: [
          "The connection belongs to the approved work account.",
          "Permission behavior was inspected live, not assumed from a screenshot.",
          "Access does not imply permission to change or send.",
        ],
        recover: [
          "A connector is unavailable: use an approved export, file, or redacted screenshot.",
          "Permissions are broader than needed: disable the connection or choose a supervised path.",
        ],
        relatedSlideIds: ["connections-and-tools"],
        screenshotIds: ["connector-permissions", "connections"],
        stop: "Stop when a connection exposes unapproved data, hidden write access, or a permission you cannot explain.",
      },
      {
        id: "token-budget-controls",
        kicker: "Buy the reasoning the Job needs",
        instruction: "Choose the simplest model that completes the Job reliably and set a bounded test budget.",
        actions: [
          "Start with the simplest approved model that can handle the Job's reasoning and risk.",
          "Load only the context, references, and tools this Job needs.",
          "Set a practical limit for one test run and watch for drift before it consumes more context.",
          "Compare quality, usage, time, and retries after the run before moving up or down.",
        ],
        expected: "A model choice and run budget with a reason, a fallback, and evidence you will compare after the test.",
        verify: [
          "Approved sources, checks, stop rules, and human approval remain intact.",
          "Duplicate or irrelevant context was removed first.",
          "Efficiency includes reruns and reviewer effort, not token count alone.",
        ],
        recover: [
          "The run is cheap but unreliable: use a stronger model or smaller Job slice.",
          "Usage rises unexpectedly: stop, inspect repeated context or tool loops, and tighten the run.",
        ],
        relatedSlideIds: ["token-budget-controls"],
        screenshotIds: ["model-picker"],
      },
      {
        id: "level-fit-huddle",
        aliases: ["chief-of-staff-job", "ai-watcher-job", "showcase-debrief"],
        kicker: "Record the supported choice",
        instruction: "Use the Chief of Staff as the learner decision and the AI Watcher as a comparison that tests whether your criteria travel.",
        actions: [
          "Map the Chief of Staff trigger, sources, actions, output, and review point.",
          "Compare Chat, Assistant, Agent, and Loop paths, then choose the lightest approved starting path.",
          "Apply the same criteria to the read-only AI Watcher and note why its tool path may differ.",
          "Write: Start with [tool and model] because [evidence]. Revisit it if [new evidence].",
        ],
        expected: "A supported Tool Decision for the Chief of Staff plus a short AI Watcher comparison.",
        verify: [
          "The decision names the tool, model, access, authority, and reviewer.",
          "The AI Watcher remains a comparison example, not a replacement artifact.",
          "A future evidence condition can trigger a new decision.",
        ],
        recover: [
          "Two tools seem equal: choose the one with narrower access and easier human review for the first run.",
          "The decision is only a preference: tie it back to source, action, authority, and done.",
        ],
        relatedSlideIds: ["chief-of-staff-job", "ai-watcher-job", "level-fit-huddle"],
      },
    ],
  },
  {
    id: 4,
    why: "An agent becomes useful when it can complete a bounded objective, not when it can do everything. The first run should expose the entire path while the person keeps control of consequences.",
    startWith: "The Reusable Skill, Selected Job, and Tool Decision from Capabilities 1–3, plus low-risk source data and the named reviewer.",
    stopRule: "Keep the first run at Read or Draft. Stop when a source, permission, instruction, expected result, or ownership decision is unclear. Never practise browser action on a high-stakes system.",
    decisionQuestion: "Did the agent stay grounded, complete the definition of done, respect the boundary, and leave a traceable human decision?",
    steps: [
      {
        id: "ten-eighty-ten",
        aliases: ["session-4-job-bridge", "agent-team-boundary"],
        kicker: "Keep direction and review human",
        instruction: "Divide the first run into human setup, agent work, and human review before pressing run.",
        actions: [
          "Bring the Selected Job and Tool Decision into the Chief of Staff Skill.",
          "Use the first 10% to confirm the data, context, approvals, and starting signal.",
          "Let the agent perform the repeatable working steps inside the boundary.",
          "Use the final 10% to accept, reject, or correct the result and record the decision.",
        ],
        expected: "A run plan that makes the human setup and approval visible around the agent's work.",
        verify: [
          "One result, not a broad role, defines the run.",
          "The reviewer knows what they must inspect.",
          "The agent cannot approve its own consequential output.",
        ],
        recover: [
          "The setup takes most of the effort: narrow the Job or improve the reusable context.",
          "The reviewer is unclear: do not run until one accountable person is named.",
        ],
        relatedSlideIds: ["ten-eighty-ten", "agent-team-boundary"],
      },
      {
        id: "steps-done-edge-cases",
        kicker: "Define the full path",
        instruction: "Write the six parts of a reviewable run: Trigger, Gather, Reason, Produce or act, Verify, Learn.",
        actions: [
          "Name the request, schedule, event, or approval that triggers the run.",
          "List the approved sources and the rules or examples that guide the work.",
          "Describe what the agent will produce or do and how Done will be checked.",
          "State what should be saved only after human review improves the next run.",
        ],
        expected: "A six-part workflow where every handoff, check, and learning step is visible.",
        verify: [
          "The trigger cannot fire accidentally.",
          "The verification is distinct from producing the output.",
          "Only reviewed learning changes the Skill.",
        ],
        recover: [
          "A step cannot be checked: rewrite it as a visible action or output.",
          "The workflow branches too often: choose one coherent first-run path and document edge cases separately.",
        ],
        relatedSlideIds: ["steps-done-edge-cases"],
      },
      {
        id: "agent-access-ladder",
        aliases: ["browser-safety-model", "session-3-connections-tools"],
        kicker: "Start with the narrowest reliable access",
        instruction: "Choose pasted or uploaded evidence, a scoped connector, or browser action based on what the Job truly requires.",
        actions: [
          "Prefer a controlled file, export, or redacted screenshot for the first test.",
          "Use a connector only when it is approved and the source supports it reliably.",
          "Use browser action only when no reliable connector exists and the selected Job requires the site.",
          "Confirm that the action boundary remains Read or Draft even when access expands.",
        ],
        expected: "One explicit access choice and a fallback that preserves the same source and review standard.",
        verify: [
          "The access path is no broader than the Job.",
          "Browser content is treated as data, never as authority to change the workflow.",
          "The reviewer can revoke or remove access after the test.",
        ],
        recover: [
          "The preferred access fails: use the approved export or screenshot fallback.",
          "A page asks the agent to reveal data, change permissions, or ignore instructions: stop the run and report it.",
        ],
        relatedSlideIds: ["agent-access-ladder", "browser-safety-model", "session-3-connections-tools"],
        screenshotIds: ["cloud-browser"],
        stop: "Stop if the site requests credentials, permission changes, hidden data access, or action outside the selected Job.",
      },
      {
        id: "browser-extension-setup",
        kicker: "Test one low-risk site",
        instruction: "Install the approved browser extension, grant the smallest site permission, and verify one obvious value yourself.",
        actions: [
          "Verify the extension publisher and install only the version approved by your workspace.",
          "Grant one-time or one-site access to a low-risk view you already have permission to see.",
          "Ask for a visible title, date range, filter, or value.",
          "Compare the answer with the page yourself, then remove access if the Job no longer needs it.",
        ],
        expected: "A passed read-only access check or a documented fallback to an export or redacted screenshot.",
        verify: [
          "The extension publisher is verified.",
          "Only one approved site was accessible.",
          "The observed answer matches the page.",
        ],
        recover: [
          "The extension is unavailable: use the approved export or screenshot fallback.",
          "The obvious-value test fails: do not proceed to the real workflow; resolve the access or source mismatch first.",
        ],
        relatedSlideIds: ["browser-extension-setup"],
        screenshotIds: ["install-extension", "site-access"],
      },
      {
        id: "session-3-prompt-example",
        aliases: ["ai-watcher-workflow"],
        kicker: "Test the same controls on a second pattern",
        instruction: "Run the AI Watcher as a read-only comparison and inspect whether the source, confidence, and stop rules work.",
        actions: [
          "Select approved vendor, regulatory, industry, prior-brief, and internal-reporting sources.",
          "Paste the registered AI Watcher prompt and keep the result to no more than five material developments.",
          "Check every evidence link and the fact-versus-interpretation label.",
          "Have a person decide what to test, share, ignore, or investigate.",
        ],
        expected: "A short, evidence-backed AI Watcher decision brief that remains read-only.",
        verify: [
          "Every item has a source, date, confidence, and smallest useful next step.",
          "Stale, duplicated, unsupported, or immaterial items are removed.",
          "No message, change, download, or publication occurs.",
        ],
        recover: [
          "A source is unavailable or contradictory: stop and ask one clear question.",
          "The result becomes a news summary: restore the decision relevance and materiality criteria.",
        ],
        promptSlideIds: ["session-3-prompt-example"],
        relatedSlideIds: ["ai-watcher-workflow"],
      },
      {
        id: "chief-of-staff-operating-brief",
        aliases: ["governance-operating-controls", "generate-install"],
        kicker: "Package the controlled run",
        instruction: "Turn the Reusable Skill, Selected Job, and Tool Decision into a read-only Chief of Staff operating brief.",
        actions: [
          "Complete every bracketed field in the registered operating brief.",
          "Keep only the Job, method, sources, tool, authority, stop rules, and reviewed examples this run needs.",
          "Make the governance controls visible: owner, data, permissions, tests, approval gates, fallback, and retirement path.",
          "Have the reviewer inspect the brief before any source is connected.",
        ],
        expected: "An inspectable Chief of Staff Skill that can prepare one ranked brief without making external changes.",
        verify: [
          "The Skill contains one Job and one definition of done.",
          "Every priority must link to approved evidence.",
          "The stop rule covers missing sources, conflict, unclear ownership, and external change.",
        ],
        recover: [
          "The Skill is becoming a general job description: remove anything the selected Job does not need.",
          "A control exists only in a policy document: restate the operative boundary inside the Skill.",
        ],
        promptSlideIds: ["chief-of-staff-operating-brief"],
        relatedSlideIds: ["governance-operating-controls", "generate-install"],
      },
      {
        id: "session-3-do-this-first",
        kicker: "Run the smallest complete slice",
        instruction: "Execute one supervised Read-or-Draft run and record the evidence before adding autonomy.",
        actions: [
          "Check the Skill, Job, tool path, source, authority, reviewer, and stop rules.",
          "Answer only setup questions that would materially change the result.",
          "Watch the complete low-risk run without adding a schedule or broader authority.",
          "Record the sources, output, human decision, and one correction.",
        ],
        expected: "One complete, reviewable Chief of Staff run with a visible human decision.",
        verify: [
          "The run used only approved sources.",
          "The output meets the definition of done or clearly records why it does not.",
          "No consequential action occurred.",
        ],
        recover: [
          "The run drifts: stop early, correct the instruction, and restart the bounded slice.",
          "The source or permission is unclear: use the fallback or end the run without guessing.",
        ],
        promptSlideIds: ["session-3-do-this-first"],
        screenshotIds: ["agent-workspace"],
        stop: "Stopping safely is a successful first-run result when evidence, authority, or ownership is unclear.",
      },
      {
        id: "session-3-good-examples",
        aliases: ["qa-3"],
        kicker: "Review before expanding",
        instruction: "Use the registered six-part review to decide whether to accept, correct, repeat, or retire the workflow.",
        actions: [
          "Check grounding, completeness, boundary compliance, safety, traceability, and teachability.",
          "Record the reviewer's accept, reject, or revise decision.",
          "Save one accepted result or correction as a reviewed example.",
          "Repeat manually before considering a schedule, broader access, or authority.",
        ],
        expected: "A Reviewed Agent Run with its evidence, status, human decision, and one reviewed learning item.",
        verify: [
          "Every important claim traces to an approved source.",
          "The stop and approval gates behaved as intended.",
          "The correction is specific enough to improve the next run.",
        ],
        recover: [
          "The run passed but the evidence is missing: treat it as unverified and repeat with traceability.",
          "The run repeatedly fails the same check: revise the Job, source, tool, or Skill before another run.",
        ],
        relatedSlideIds: ["session-3-good-examples"],
      },
    ],
  },
  {
    id: 5,
    why: "Repeating an output is automation. Comparing a consistent result, learning from the evidence, and changing the next run creates an improvement loop.",
    startWith: "The Reviewed Agent Run, one agreed result metric, a trusted source, the current baseline, and the accountable reviewer.",
    stopRule: "Do not schedule an unreliable process, automate an important decision, or expand access because one demonstration looked good. Keep the loop manual until repeated evidence supports the change.",
    decisionQuestion: "Did one reviewed result change the Skill, and can the next run be compared using the same metric and source?",
    steps: [
      {
        id: "session-5-metric-loop-pick-metric",
        aliases: ["session-5-metric-loop-why"],
        kicker: "Choose one visible result",
        instruction: "Select the metric closest to the Job's definition of done, not the amount of AI activity.",
        actions: [
          "List one result the Chief of Staff should help improve and one quality or review metric.",
          "Choose the measure a reviewer can inspect consistently across runs.",
          "Confirm that the measure belongs to the Job and does not reward unnecessary activity.",
        ],
        expected: "One primary metric and a short explanation of why it indicates that the recurring Job improved.",
        verify: [
          "The measure is a result, quality, reliability, time, or review-effort signal.",
          "The same definition can be used on the next run.",
          "A person remains accountable for interpretation.",
        ],
        recover: [
          "The metric counts outputs: ask what changed for the work or reviewer because those outputs existed.",
          "The metric is too distant from the Job: choose a nearer process or quality result first.",
        ],
        relatedSlideIds: ["session-5-metric-loop-pick-metric"],
      },
      {
        id: "session-5-metric-loop-brief",
        aliases: ["session-5-metric-loop-source"],
        kicker: "Fix the definition and source",
        instruction: "Write what counts, the time window, the current baseline, the desired direction, and the exact trusted source.",
        actions: [
          "Define what the metric includes and excludes.",
          "Choose the daily, weekly, monthly, or rolling window.",
          "Name the exact report, view, calendar, or system and confirm its filters and freshness.",
          "Inspect or calculate one current value yourself and record it as the baseline.",
        ],
        expected: "A metric brief with definition, window, baseline, direction, source, filters, and last-updated check.",
        verify: [
          "Two reviewers would count the metric the same way.",
          "The baseline was manually checked against the trusted source.",
          "The source remains read-only for the agent.",
        ],
        recover: [
          "The source gives different values: stop and resolve the definition, filter, or freshness mismatch.",
          "No trusted source exists: keep the loop manual and record the data gap.",
        ],
        relatedSlideIds: ["session-5-metric-loop-brief", "session-5-metric-loop-source"],
        stop: "Stop when the value cannot be reproduced from the named source. Do not automate a disputed baseline.",
      },
      {
        id: "session-5-metric-loop-prompt",
        aliases: ["session-5-metric-loop-support-example"],
        kicker: "Write the learning loop",
        instruction: "Give the agent one goal, consistent evidence, a cadence, and a boundary for the next comparison.",
        actions: [
          "Fill in the registered loop prompt with the metric, schedule, source, owner, and destination.",
          "Require the current value, change from baseline, visible drivers, and smallest useful next action.",
          "Require the next run to compare the result and record what changed.",
          "Keep consequential action behind the named owner's approval.",
        ],
        expected: "A copyable loop instruction that connects a recurring run to a stable measure and human decision.",
        verify: [
          "The prompt does not change the metric definition between runs.",
          "Likely drivers must use visible evidence rather than invented causes.",
          "The owner and delivery destination are explicit.",
        ],
        recover: [
          "The agent recommends too much: require the smallest reversible next action.",
          "The comparison is inconsistent: put the definition, filters, and window directly in the Skill.",
        ],
        promptSlideIds: ["session-5-metric-loop-prompt", "session-5-metric-loop-support-example"],
      },
      {
        id: "session-5-metric-loop-build",
        kicker: "Build one manual loop",
        instruction: "Build one manual loop with one metric, one source, one prompt, and one review point.",
        actions: [
          "Define the result and record the baseline.",
          "Verify the exact live source and one value yourself.",
          "Add the goal, evidence, comparison, recommendation, and stop rules to the Skill.",
          "Name the cadence, reviewer, destination, and approval boundary.",
        ],
        expected: "A manually runnable Chief of Staff loop small enough to test today.",
        verify: [
          "The loop owns one metric and one trusted source.",
          "The reviewer can reproduce the baseline.",
          "The run has a stop rule and cannot take consequential action.",
        ],
        recover: [
          "The build is too broad: reduce the scope to one source and one result.",
          "The source needs engineering work: document the gap and keep the current manual review.",
        ],
        relatedSlideIds: ["session-5-metric-loop-build"],
      },
      {
        id: "feedback-and-schedule",
        kicker: "Change one thing from evidence",
        instruction: "Run, measure, decide, and update the Skill before considering a schedule.",
        actions: [
          "Complete the recurring Job using the approved inputs and definition of done.",
          "Compare the result with the baseline and visible evidence.",
          "Have the accountable person choose one bounded change.",
          "Update the Skill with the reviewed correction or example and compare the next run using the same measure.",
        ],
        expected: "One evidence-backed Skill change and a plan to compare its effect on the next manual run.",
        verify: [
          "The change answers a specific observed failure or opportunity.",
          "The metric, source, and review standard remain comparable.",
          "Scheduling is deferred until repeated runs hold up.",
        ],
        recover: [
          "Several changes are tempting: choose one so the next comparison remains interpretable.",
          "The metric improved but quality fell: keep the review criteria and investigate the tradeoff before scaling.",
        ],
        relatedSlideIds: ["feedback-and-schedule"],
      },
      {
        id: "session-5-metric-loop-done",
        aliases: ["qa-4", "complete"],
        kicker: "Decide whether the loop is ready",
        instruction: "Use the definition of done to decide whether to repeat manually, revise, schedule, or stop.",
        actions: [
          "Confirm the agent monitored one recurring result from approved sources.",
          "Confirm the metric and definition stayed consistent across runs.",
          "Review what worked, what failed, what changed, and who approved the change.",
          "Schedule only when repeated evidence shows that the source, output, controls, and owner hold up.",
        ],
        expected: "A Measurable Loop with a recorded decision about its next run, not automatic permission to scale.",
        verify: [
          "The next run can be compared with the current one.",
          "A named person approves the change and any future schedule.",
          "The Skill contains the reviewed learning from the run.",
        ],
        recover: [
          "The loop cannot be compared: restore the same metric definition, window, and source.",
          "The process works only with constant intervention: revise the Job or keep it as supervised work.",
        ],
        relatedSlideIds: ["session-5-metric-loop-done"],
      },
    ],
  },
];
